import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import Pagination from '../../../components/common/Pagination';
import ContentLoader from '../../../components/common/ContentLoader';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import { Modal, Button, Form } from 'react-bootstrap';
import { buildVendorProfileUrl } from '../../../utils/vendorProfileUrl';
import { formatDate } from '../../../utils/dateUtils';

const VendorListing = () => {
    const { type } = useParams();
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination states
    const [page, setPage] = useState(() => {
        const saved = sessionStorage.getItem(`vendor_page_${type || 'all'}`);
        return saved ? parseInt(saved) : 1;
    });
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState(() => {
        const saved = sessionStorage.getItem(`vendor_filters_${type || 'all'}`);
        return saved ? JSON.parse(saved) : { location: '', mobile: '', company_name: '', email: '', plan_id: '' };
    });

    useEffect(() => {
        sessionStorage.setItem(`vendor_page_${type || 'all'}`, page.toString());
    }, [page, type]);

    useEffect(() => {
        sessionStorage.setItem(`vendor_filters_${type || 'all'}`, JSON.stringify(filters));
    }, [filters, type]);
    const [plans, setPlans] = useState([]);

    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newVendor, setNewVendor] = useState({
        name: '', phone: '', email: '', address: '', city: '', category: ''
    });

    const handleCloseModal = () => setShowAddModal(false);
    const handleShowModal = () => setShowAddModal(true);

    const handleNewVendorChange = (e) => {
        setNewVendor({ ...newVendor, [e.target.name]: e.target.value });
    };

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                ...filters,
                ...(type ? { type } : {}),
                plan_id: filters.plan_id,
                page: page,
                limit: 10
            });
            const response = await superAdminApiClient.get(`/superadmin/vendors?${queryParams}`);
            setVendors(response.data.vendors);
            setTotalPages(response.data.total_pages);
            setTotalRecords(response.data.total_records);
        } catch (error) {
            console.error('Error fetching vendors', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddVendorSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await superAdminApiClient.post('/superadmin/vendors', newVendor);
            if (response.data.success) {
                toast.success('Vendor added successfully and onboarding email sent.');
                setShowAddModal(false);
                setNewVendor({ name: '', phone: '', email: '', address: '', city: '', category: '' });
                // trigger re-fetch
                setPage(1);
                await fetchVendors();
            }
        } catch (error) {
            console.error('Error adding vendor:', error);
            toast.error(error.response?.data?.message || 'Failed to add vendor');
        } finally {
            setSubmitting(false);
        }
    };

    const handleImpersonate = async (vendorId) => {
        try {
            const response = await superAdminApiClient.post(`/superadmin/vendors/${vendorId}/impersonate`);
            if (response.data.success) {
                window.open(response.data.redirectUrl, '_blank');
            }
        } catch (error) {
            console.error('Error impersonating vendor:', error);
            toast.error('Failed to access vendor profile.');
        }
    };

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await superAdminApiClient.get('/superadmin/plans');
                if (response.data.success) {
                    setPlans(response.data.plans);
                }
            } catch (error) {
                console.error('Error fetching plans', error);
            }
        };
        fetchPlans();
    }, []);

    useEffect(() => {
        // Debounce fetching if filters change fast
        const handler = setTimeout(() => {
            fetchVendors();
        }, 500);
        return () => clearTimeout(handler);
    }, [filters, type, page]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setPage(1); // Reset page on filter change
    };

    return (
        <React.Fragment>
            <SEO
                title="Vendor Listing | Mangoloops"
                description="List of all vendors on Mangoloops."
            />
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">{type ? type.charAt(0).toUpperCase() + type.slice(1) : 'All'} Vendors</h4>
                        {(type && type === "trial") && (
                            <div className="page-title-right">
                                <Button variant="success" onClick={handleShowModal}>
                                    <i className="bx bx-plus me-1"></i> Add New Vendor
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="row mb-3 gy-2 gy-md-0">
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Company Name" name="company_name" value={filters.company_name} onChange={handleFilterChange} />
                </div>
                <div className="col-md-3">
                    <input type="text" className="form-control" placeholder="Email" name="email" value={filters.email} onChange={handleFilterChange} />
                </div>
                <div className="col-md-2">
                    <input type="text" className="form-control" placeholder="Mobile" name="mobile" value={filters.mobile} onChange={handleFilterChange} />
                </div>
                <div className="col-md-2">
                    <input type="text" className="form-control" placeholder="Location" name="location" value={filters.location} onChange={handleFilterChange} />
                </div>
                <div className="col-md-2 mt-2 mt-md-0">
                    <Form.Select name="plan_id" value={filters.plan_id} onChange={handleFilterChange}>
                        <option value="">All Plans</option>
                        {plans.map(plan => (
                            <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                    </Form.Select>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <ContentLoader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>SR No.</th>
                                                <th>Registered On</th>
                                                <th>Company Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                                <th>Location</th>
                                                <th>Active Subscription</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {vendors.length > 0 ? vendors.map((v, index) => (
                                                <tr key={v.id}>
                                                    <td>{index + 1}</td>
                                                    <td>{v.createdAt ? formatDate(v.createdAt, 'DD-MM-YYYY') : '-'}</td>
                                                    <td>{v.company_name ? v.company_name : (v.name ? v.name : "-")}</td>
                                                    <td>{v.email ? v.email : '-'}</td>
                                                    <td>{v.phone}</td>
                                                    <td>{v.company_city ? v.company_city : '-'}</td>
                                                    <td>{v.Subscriptions && v.Subscriptions[0]?.Plan?.name ? v.Subscriptions[0].Plan.name : <span className="text-muted">None</span>}</td>
                                                    <td>{v.is_verified ? <span className="badge bg-success">Verified</span> : <span className="badge bg-warning">Pending</span>}</td>
                                                    <td>
                                                        <Link to={`/superadmin/vendors/detail/${v.id}`} className="btn btn-sm btn-primary me-2">View</Link>
                                                        <a
                                                            href={buildVendorProfileUrl(v)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-info me-2"
                                                        >
                                                            Public Profile
                                                        </a>
                                                        <button onClick={() => handleImpersonate(v.id)} className="btn btn-sm btn-secondary">
                                                            <i className="bx bx-log-in-circle me-1"></i> Access Profile
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="7" className="text-center">No vendors found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing {vendors.length} of {totalRecords} vendors
                                        </div>
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            onPageChange={(p) => setPage(p)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Vendor Modal */}
            <Modal show={showAddModal} onHide={handleCloseModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Add New Vendor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddVendorSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Name / Company Name</Form.Label>
                            <Form.Control type="text" name="name" required value={newVendor.name} onChange={handleNewVendorChange} placeholder="Enter name" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control type="email" name="email" required value={newVendor.email} onChange={handleNewVendorChange} placeholder="Enter email" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Phone</Form.Label>
                            <Form.Control type="text" name="phone" required value={newVendor.phone} onChange={handleNewVendorChange} placeholder="Enter phone number" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select name="category" required value={newVendor.category} onChange={handleNewVendorChange}>
                                <option value="">Select Category</option>
                                <option value="Holidays">Holidays</option>
                                <option value="Hotels">Hotels</option>
                                <option value="Cab">Cab</option>
                                <option value="Tirth Yatra">Tirth Yatra</option>
                                <option value="Medical Tourism">Medical Tourism</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Address</Form.Label>
                            <Form.Control type="text" name="address" required value={newVendor.address} onChange={handleNewVendorChange} placeholder="Enter full address" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control type="text" name="city" required value={newVendor.city} onChange={handleNewVendorChange} placeholder="Enter city" />
                        </Form.Group>

                        <div className="text-end mt-4">
                            <Button variant="secondary" onClick={handleCloseModal} className="me-2">Cancel</Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? 'Adding...' : 'Add Vendor'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

        </React.Fragment>
    );
};

export default VendorListing;
